<template>
  <div class="relative">
    <div :id="`questions-${section}`" class="questions-wrap" ref="questionsWrap">
      <div class="questions" ref="questions">
        <Question v-for="(question, questionKey) in questions"
                  :ref="questionKey"
                  :key="questionKey"
                  :id="`question-${questionKeyToInt(questionKey)}-wrap`"
                  :section="section"
                  :question="question"
                  :question-key="questionKeyToInt(questionKey)"
                  :is-current="currentQuestion === questionKey"
                  :class="{current: currentQuestion === questionKey}"
                  @answer="handleAnswer"
        />
      </div>
    </div>

    <div class="questions-nav">
      <button type="button" role="button" class="link link-no-underline btn-prev-section"
         :class="{disabled: !hasPrevSection}"
         @click.prevent="hasPrevSection && goToPrevSection()">
        &larr;
      </button>

      <button type="button" role="button" class="link link-no-underline btn-prev-question"
         :class="{disabled: !hasPrevQuestion}"
         @click.prevent="hasPrevQuestion && goToPrevQuestion()">
        &uarr;
      </button>

      <button type="button" role="button" class="link link-no-underline btn-next-question"
         :class="{disabled: !hasNextQuestion}"
         @click.prevent="hasNextQuestion && goToNextQuestion()">
        &darr;
      </button>

      <button type="button" role="button" class="link link-no-underline btn-next-section"
         :class="{disabled: !hasNextSection}"
         @click.prevent="hasNextSection && goToNextSection()">
        &rarr;
      </button>
    </div>
  </div>
</template>

<script>
import Html from "@/lib/Html";
import Question from "@/components/Survey/Question"
import {mapActions} from "vuex"
import {buildTriagePayload} from "@/store/survey"

export default {
  components: {
    Question,
  },

  props: {
    section: {
      type: Number,
      required: true,
    },

    previousRoute: {
      type: String,
      required: true,
    },

    nextRoute: {
      type: [String, Function],
      required: true,
    },

    nextCallback: {
      type: Function,
    }
  },

  data() {
    return {
      currentQuestion: null,
      hasPrevQuestion: false,
      hasPrevSection: false,
      hasNextQuestion: false,
      hasNextSection: false,
    };
  },

  computed: {
    navigatingBackward() {
      return this.$root.$children[0].navigatingBackward;
    },

    questions() {
      return this.getSectionQuestions(this.section);
    },
  },

  mounted() {
    window.addEventListener("keydown", this.onKeyDown, false);
    window.addEventListener("keyup", this.onKeyUp, false);
    window.addEventListener("resize", this.onResize, false);

    this.$refs.questionsWrap.classList.add("loaded");

    this.$nextTick(this.init);
  },

  updated() {
    this.$nextTick(function () {
      const questionId = this.questions[this.currentQuestion].id;
      const step = this.section + 1;
      this.$gtm.trackView(`Step ${step} Question ${questionId}`, `/step-${step}-question-${questionId}`);
    });
  },

  destroyed() {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("resize", this.onResize);
  },

  methods: {
    ...mapActions("survey", [
      "postTriageState",
    ]),

    /**
     * Prevent browser arrow-key scrolling when navigating questions/sections.
     *
     * @return {void}
     */
    onKeyDown(e) {
      // Don't interfere with meta keys.
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) {
        return;
      }

      if (
        (e.key === "ArrowUp" && this.hasPrevQuestion) ||
        (e.key === "ArrowDown" && this.hasNextQuestion) ||
        (e.key === "ArrowLeft" && this.hasPrevSection) ||
        (e.key === "ArrowRight" && this.hasNextSection)
      ) {
        e.preventDefault();
      }
    },

    /**
     * Allow questions/sections to be navigated with the keyboard.
     *
     * @return {void}
     */
    onKeyUp(e) {
      if (e.target.classList.contains("range-slider-knob")) {
        return;
      }

      switch (e.key) {
        case "ArrowUp":
          if (this.hasPrevQuestion) {
            this.goToPrevQuestion();
          }
          break;

        case "ArrowDown":
          if (this.hasNextQuestion) {
            this.goToNextQuestion();
          }
          break;

        case "ArrowLeft":
          if (this.hasPrevSection) {
            this.goToPrevSection();
          }
          break;

        case "ArrowRight":
          if (this.hasNextSection) {
            this.goToNextSection();
          }
          break;
      }
    },

    onResize() {
      if (!this.currentQuestion) {
        return;
      }

      const questionElement = this.$refs[this.currentQuestion][0].$el;

      this.$refs.questionsWrap.style.height = Html.elementHeightWithMargin(questionElement) + "px";
    },

    init() {
      if (this.navigatingBackward) {
        this.jumpToLastQuestion();
      } else {
        this.setCurrentQuestion(undefined, true);
      }
    },

    /**
     * Set the current/active question
     *
     * @param {string|undefined} question
     * @param {boolean|undefined} withoutAnimation
     * @return {void}
     */
    setCurrentQuestion(question, withoutAnimation = false) {
      this.currentQuestion = question || Object.keys(this.questions)[0];

      const questionElement = this.$refs[this.currentQuestion][0].$el;

      this.$refs.questionsWrap.style.height = Html.elementHeightWithMargin(questionElement) + "px";

      this.slideToQuestion(questionElement, withoutAnimation);

      this.hasPrevQuestion = this.canGoToPrevQuestion();
      this.hasPrevSection = this.canGoToPrevSection();
      this.hasNextQuestion = this.canGoToNextQuestion();
      this.hasNextSection = this.canGoToNextSection();
    },

    slideToQuestion(questionElement, withoutAnimation) {
      const scroller = window.scroller();

      if (withoutAnimation) {
        scroller(questionElement, {
          container: `#questions-${this.section}`,
          cancelable: false,
          duration: -1,
        });
      } else {
        scroller(questionElement, {
          container: `#questions-${this.section}`,
          cancelable: false,
          easing: [0.77, 0.2, 0.05, 1],
          duration: 500,
        });
      }
    },

    /**
     * Jump to the last unanswered question.
     *
     * @return {void}
     */
    jumpToLastQuestion() {
      const answers = this.$store.state.survey.answers[`section${this.section}`];

      let startQuestion = undefined;

      for (let i in this.questions) {
        if (this.questions.hasOwnProperty(i)) {
          if (answers[i] !== null) {
            startQuestion = i;
          } else {
            break;
          }
        }
      }

      if (startQuestion !== undefined) {
        this.setCurrentQuestion(startQuestion, true);
      }
    },

    /**
     * Get the key for the previous question.
     *
     * @param {string} questionKey
     * @returns {string|undefined}
     */
    getPrevQuestionKey(questionKey) {
      let i, previous;

      for (i in this.questions) {
        if (this.questions.hasOwnProperty(i)) {
          if (i === questionKey) {
            break;
          }

          previous = i;
        }
      }

      return previous;
    },

    /**
     * Get the key for the next question.
     *
     * @param {string} questionKey
     * @returns {string|undefined}
     */
    getNextQuestionKey(questionKey) {
      let i, next, takeNext = false;

      for (i in this.questions) {
        if (this.questions.hasOwnProperty(i)) {
          if (i === questionKey) {
            takeNext = true;
            continue;
          }

          if (takeNext) {
            next = i;
            break;
          }
        }
      }

      if (typeof this.questions[next] !== 'undefined') {
        const skipQuestion = this.questions[next].skipQuestion

        if (skipQuestion && skipQuestion(this.$store.state.survey)) {
          return this.getNextQuestionKey(next)
        }
      }
      
      return next;
    },

    /**
     * Determine if there is a previous question in this section to go to.
     *
     * @returns {boolean}
     */
    canGoToPrevQuestion() {
      return this.getPrevQuestionKey(this.currentQuestion) !== undefined;
    },

    /**
     * Determine if there is a next question in this section to go to.
     *
     * @returns {boolean}
     */
    canGoToNextQuestion() {
      const answers = this.$store.state.survey.answers[`section${this.section}`];

      return answers[this.currentQuestion] !== null && this.getNextQuestionKey(this.currentQuestion) !== undefined;
    },

    /**
     * Determine if we can go to the previous section.
     *
     * @returns {boolean}
     */
    canGoToPrevSection() {
      return !!this.previousRoute;
    },

    /**
     * Determine if we can go to the next section.
     *
     * @returns {boolean}
     */
    canGoToNextSection() {
      return this.sectionQuestionsAnswered(this.section, this.questions);
    },

    /**
     * Go to the previous question, or go back to the previous route.
     *
     * @return {void}
     */
    goToPrevQuestion() {
      const previousQuestionKey = this.getPrevQuestionKey(this.currentQuestion);

      if (previousQuestionKey !== undefined) {
        this.setCurrentQuestion(previousQuestionKey);
      } else {
        this.goToPrevSection();
      }
    },

    /**
     * Go to the previous section.
     *
     * @return {void}
     */
    goToPrevSection() {
      this.$router.push({name: this.previousRoute})
    },

    /**
     * Go to the next question, or to the next route if there are no more questions in this section.
     *
     * @return {void}
     */
    goToNextQuestion() {
      if (this.canGoToNextQuestion()) {
        if (!this.isScreen("md")) {
          const scroller = window.scroller();

          scroller("#progress-bar", {
            cancelable: false,
            easing: [0.77, 0.2, 0.05, 1],
            duration: 500,
          });
        }

        this.setCurrentQuestion(
          this.getNextQuestionKey(this.currentQuestion)
        );
      } else if (this.canGoToNextSection()) {
        this.goToNextSection();
      }
    },

    /**
     * Go to the next section.
     *
     * @return {void}
     */
    goToNextSection() {
      if (typeof this.nextRoute === "string") {
        this.$router.push({name: this.nextRoute});
      } else {
        this.nextRoute();
      }
    },

    /**
     * Handle an answered question.
     *
     * @param {number} section
     * @param {number} questionKey
     * @param {object} question
     * @param {boolean|number|string} answer
     * @return {void}
     */
    handleAnswer(section, questionKey, question, answer, confirmed) {
      const requiresConfirmation = question.confirmation && question.confirmation(answer);
      if (requiresConfirmation) {
        this.onResize();
      }

      this.$emit("answer", section, questionKey, question, answer, confirmed);

      if (question.needsAppointment && question.needsAppointment(answer)) {
        this.setNeedsAppointment(true);
      }

      const callback = () => {
        if (question.goStraightToAppointment && question.goStraightToAppointment(answer)) {
          this.setNeedsAppointment(true);
          this.$router.push(this.routes.appointmentNeeded);
          return;
        }

        if (requiresConfirmation && confirmed || !requiresConfirmation) {
          this.goToNextQuestion();
        }
      };

      const payload = buildTriagePayload(this.$store.state.survey);

      if (payload.isComplete) {
        this.postTriageState({
          payload,
          onSuccess: callback,
          onError: callback
        });
      } else {
        callback();
      }
      
    },
  },
}
</script>

<style lang="scss">
.questions-wrap {
  overflow: hidden;
  transition: height 500ms ease;

  &:not(.loaded) {
    .question:not(.current) {
      display: none;
    }
  }
}

.questions-nav {
  display: none;
  position: absolute;
  top: 0.5rem;
  right: 0;
  width: 50px;
  height: 50px;
  background-color: rgba($color-chathams-blue, 0.75);
  border-radius: 0.5rem;

  .link {
    color: $color-white;

    &.disabled {
      opacity: 0.25;
    }

    &:not(.disabled) {
      @include focusHover {
        color: $color-white;
      }

      @include active {
        color: $link-color-active;
      }
    }
  }
}

.btn-prev-section,
.btn-prev-question,
.btn-next-question,
.btn-next-section {
  position: absolute;
}

.btn-prev-section {
  top: 50%;
  left: 0.25rem;
  transform: translateY(-50%);
}

.btn-prev-question {
  top: 0.25rem;
  left: 50%;
  transform: translateX(-50%);
}

.btn-next-section {
  top: 50%;
  right: 0.25rem;
  transform: translateY(-50%);
}

.btn-next-question {
  bottom: 0.25rem;
  left: 50%;
  transform: translateX(-50%);
}
</style>
